import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export const REQUEST_STATUS = {
  IDLE: 'idle',
  LOADING: 'loading',
  SUCCESS: 'success',
  ERROR: 'error'
};

/**
 * Small shared state machine for one async request: idle → loading →
 * success | error (userregistrations.md U1.5). Used for loading lists,
 * submitting forms and anything in between.
 *
 * - `run(task)` resolves to `{ ok: true, data }` or `{ ok: false, error }` —
 *   it never throws, so callers can branch without try/catch.
 * - Only the LATEST call may update state. A slower, older response (e.g. the
 *   facilities of a group the user already switched away from) is ignored and
 *   resolves to `{ ok: false, stale: true }`.
 * - Nothing is set after unmount.
 */
const useRequestState = ({
  initialStatus = REQUEST_STATUS.IDLE,
  initialData = null
} = {}) => {
  const [state, setState] = useState({
    status: initialStatus,
    data: initialData,
    error: null
  });
  const mountedRef = useRef(true);
  const latestCallRef = useRef(0);
  const initialDataRef = useRef(initialData);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const run = useCallback(async (task, { keepData = true } = {}) => {
    latestCallRef.current += 1;
    const callId = latestCallRef.current;
    const isCurrent = () =>
      mountedRef.current && callId === latestCallRef.current;

    setState(prev => ({
      status: REQUEST_STATUS.LOADING,
      data: keepData ? prev.data : null,
      error: null
    }));

    try {
      const data = await task();
      if (!isCurrent()) {
        return { ok: false, stale: true };
      }
      setState({ status: REQUEST_STATUS.SUCCESS, data, error: null });
      return { ok: true, data };
    } catch (error) {
      if (!isCurrent()) {
        return { ok: false, stale: true };
      }
      setState(prev => ({
        status: REQUEST_STATUS.ERROR,
        data: keepData ? prev.data : null,
        error
      }));
      return { ok: false, error };
    }
  }, []);

  // Setting data directly also invalidates any request still in flight.
  const setData = useCallback(data => {
    latestCallRef.current += 1;
    setState({ status: REQUEST_STATUS.SUCCESS, data, error: null });
  }, []);

  const reset = useCallback(() => {
    latestCallRef.current += 1;
    setState({
      status: REQUEST_STATUS.IDLE,
      data: initialDataRef.current,
      error: null
    });
  }, []);

  return useMemo(
    () => ({
      ...state,
      isIdle: state.status === REQUEST_STATUS.IDLE,
      isLoading: state.status === REQUEST_STATUS.LOADING,
      isSuccess: state.status === REQUEST_STATUS.SUCCESS,
      isError: state.status === REQUEST_STATUS.ERROR,
      run,
      setData,
      reset
    }),
    [state, run, setData, reset]
  );
};

export default useRequestState;
